import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { getCurrentDate } from '@shared/utils'
import { useRestaurantModel } from '@entities/restaurant'
import { useOrderModel } from '@entities/order'
import { sendMessage as telegramSendMessage } from '@features/telegram'
import { AppButton } from '@shared/ui'
import { useMenuModel } from '@entities/menu'

// Amplitude
import { amplitudeLogEvent, amplitudeEvents } from '@shared/lib'

interface ITelegaReq {
  shopCode: string
  message: string
  messageType: string
  sessionId: null | number
  userId?: number
}


export default /*#__PURE__*/_defineComponent({
  __name: 'MenuCartCallWaiter',
  emits: ['toggleCartCallWaiterModal'],
  setup(__props, { emit }) {



const restaurantModel = useRestaurantModel()
const orderModel = useOrderModel()
const menuModel = useMenuModel()

const COOLDOWN_SECONDS = 60
const cooldownLeft = ref(0)
let cooldownTimer: ReturnType<typeof setInterval> | null = null
let cooldownEndAt = 0

const getStorageKey = () => `cartWaiterCooldown:${restaurantModel.code || restaurantModel.tableCode || 'default'}`

const readStoredCooldown = () => {
  if (typeof window === 'undefined') {
    return 0
  }
  const raw = window.localStorage.getItem(getStorageKey())
  if (!raw) {
    return 0
  }
  const parsed = parseInt(raw, 10)
  return Number.isFinite(parsed) ? parsed : 0
}

const storeCooldown = (timestamp: number) => {
  if (typeof window === 'undefined') {
    return
  }
  window.localStorage.setItem(getStorageKey(), String(timestamp))
}

const clearStoredCooldown = () => {
  if (typeof window === 'undefined') {
    return
  }
  window.localStorage.removeItem(getStorageKey())
}

const isCooldownActive = computed(() => cooldownLeft.value > 0)

const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

const formattedTime = computed(() => formatTime(cooldownLeft.value))
const formattedTimeChars = computed(() => formattedTime.value.split(''))

const stopCooldown = () => {
  if (cooldownTimer) {
    clearInterval(cooldownTimer)
    cooldownTimer = null
  }
}

const updateCooldownLeft = () => {
  if (!cooldownEndAt) {
    cooldownLeft.value = 0
    return
  }

  const secondsLeft = Math.ceil((cooldownEndAt - Date.now()) / 1000)
  if (secondsLeft <= 0) {
    cooldownLeft.value = 0
    cooldownEndAt = 0
    clearStoredCooldown()
    stopCooldown()
    return
  }

  cooldownLeft.value = secondsLeft
}

const startCooldown = () => {
  stopCooldown()
  cooldownEndAt = Date.now() + COOLDOWN_SECONDS * 1000
  storeCooldown(cooldownEndAt)
  updateCooldownLeft()

  cooldownTimer = setInterval(updateCooldownLeft, 1000)
}

onMounted(() => {
  const storedEnd = readStoredCooldown()
  if (storedEnd && storedEnd > Date.now()) {
    cooldownEndAt = storedEnd
    updateCooldownLeft()
    cooldownTimer = setInterval(updateCooldownLeft, 1000)
  } else {
    clearStoredCooldown()
  }
})

onUnmounted(() => {
  stopCooldown()
})

// Получает модификаторы с количеством для элемента корзины
const getModifiersWithCount = (item: any): Array<{ modifier: any, count: number }> => {
  if (!item?.selectedModifiers || item.selectedModifiers.length === 0) {
    return []
  }

  const available: any[] = []
  if (Array.isArray(item.modifiers)) {
    available.push(...item.modifiers)
  }
  if (Array.isArray(item.modifier_groups)) {
    item.modifier_groups.forEach((group: any) => {
      if (Array.isArray(group.modifiers)) {
        available.push(...group.modifiers)
      }
    })
  }

  if (!available.length) {
    return []
  }

  // Группируем модификаторы по ID и считаем количество
  const modifierCountMap = new Map<number, { modifier: any, count: number }>()

  item.selectedModifiers.forEach((modifierId: number) => {
    const modifier = available.find((m: any) => m.id === modifierId)
    if (modifier) {
      const existing = modifierCountMap.get(modifierId)
      if (existing) {
        existing.count += 1
      } else {
        modifierCountMap.set(modifierId, { modifier, count: 1 })
      }
    }
  })

  return Array.from(modifierCountMap.values())
}

// Получает название размера по ID
const getSizeName = (productId: number, sizeId: string | null): string | null => {
  if (!sizeId) {
    return null
  }

  // Ищем продукт в меню
  let product: any = null
  menuModel.menuUser.forEach((category: any) => {
    const found = category.products.find((p: any) => p.id === productId)
    if (found) {
      product = found
    }
  })

  if (product && Array.isArray(product.size)) {
    const sizeOption = product.size.find((s: any) => s.id === sizeId)
    if (sizeOption) {
      return sizeOption.name
    }
  }

  return sizeId
}

const sendMessage = async () => {
  if (isCooldownActive.value) {
    return
  }

  startCooldown()

  let textOfMessage
  if (orderModel.orderData?.waiter?.user) {
    textOfMessage =
    '*Вызов официанта для заказа*' +
    '\n' +
    `*🖐 🖐Номер столика: ${restaurantModel.tableCode}*` +
    '\n' +
    `Официант: ${orderModel.orderData.waiter.display_name ?? orderModel.orderData.waiter.name}`
  } else {
    textOfMessage =
    '*!!!Вызов официанта для заказа!!!*' +
    '\n' +
    `*🖐 🖐Номер столика: ${restaurantModel.tableCode}*`
  }

  const bullet = '\u2022'
  const rubleSymbol = '\u20BD'
  const products = menuModel.cart.map((i) => {
    const name = i.internal_name ?? i.our_name ?? i.name
    const count = i.inCart
    const itemPrice = (i.price * count) / 100

    // Получаем модификаторы с количеством
    const modifiersWithCount = getModifiersWithCount(i)
    let modifiersText = ''
    if (modifiersWithCount.length > 0) {
      const modifierNames = modifiersWithCount.map(({ modifier, count: modCount }) => {
        return modCount > 1 ? `+ ${modifier.name} (${modCount}шт)` : `+ ${modifier.name}`
      })
      modifiersText = '\n   ' + modifierNames.join('\n   ')
    }

    // Получаем размер, если есть
    const sizeName = getSizeName(i.id, i.selectedSize)
    const sizeText = sizeName ? `, размер: ${sizeName}` : ''

    return ` ${bullet} ${name} (${count})${sizeText} - ${itemPrice}${rubleSymbol}${modifiersText}`
  })

  const sum = menuModel.cart.reduce((sum, i) => sum + (i.price / 100) * i.inCart, 0)

  const productsText = products.join('\n')

  const total = `💰 Итого: ${sum}${rubleSymbol}`

  const finalText = textOfMessage + '\n' + '📋 Предварительный заказ: ' + '\n' + productsText + '\n' + total + '\n' + 'Время: ' + getCurrentDate()

  const telegaReq: ITelegaReq = {
    shopCode: restaurantModel.code,
    message: finalText,
    messageType: 'WAITER_CALL',
    sessionId: orderModel.orderData?.session ? parseInt(orderModel.orderData.session) : null
  }

  if (orderModel.orderData?.waiter) {
    telegaReq.userId = orderModel.orderData.waiter.id
  }

  await telegramSendMessage(telegaReq)

  amplitudeLogEvent(amplitudeEvents.CART_CALL_WAITER)

  emit('toggleCartCallWaiterModal')
}

return { __sfc: true,emit, restaurantModel, orderModel, menuModel, COOLDOWN_SECONDS, cooldownLeft, cooldownTimer, cooldownEndAt, getStorageKey, readStoredCooldown, storeCooldown, clearStoredCooldown, isCooldownActive, formatTime, formattedTime, formattedTimeChars, stopCooldown, updateCooldownLeft, startCooldown, getModifiersWithCount, getSizeName, sendMessage, AppButton }
}

})